<template>
  <HLContentWrap
    full-screen
    namespace="kaizen"
  >
    <div class="kaizen-app">
      <!-- Loading Screen -->
      <LoadingScreen v-if="loading" />

      <!-- Main Application -->
      <div v-else>
        <!-- Authentication Pages (Login, Register, Forgot Password, etc.) -->
        <router-view v-if="!isAuthenticated || isAuthPage" />

        <!-- Main Layout (Dashboard and other authenticated pages) -->
        <Sidebar v-else>
          <div class="flex flex-col overflow-hidden h-screen">
            <TopBar />

            <main
              class="flex-1 min-w-0 overflow-x-hidden overflow-y-auto transition-all duration-300  kaizen-app-content "
              :class="{'kaizen-app-dashboard': isDashBoard}"
            >
              <div class="min-w-0">
                <router-view />
              </div>
            </main>
          </div>
        </Sidebar>
      </div>

      <!-- Small-screen warning: app continues to work, modal is dismissible -->
      <KaizenModal
        :show="showSmallScreenWarning"
        title="Small screen detected"
        subtitle="Kaizen is optimized for screens at least 768px wide."
        icon-color="orange"
        :width="420"
        @close="dismissSmallScreenWarning"
      >
        <template #icon>
          <MaterialWarningIcon />
        </template>
        <div class="small-screen-warning__body">
          <HLText size="sm">
            You can keep using the app, but some views may look cramped or behave unexpectedly below this width.
          </HLText>
        </div>
      </KaizenModal>

      <!-- Global Confirmation Dialog -->
      <ConfirmationDialog
        :is-open="confirmation.isOpen.value"
        :title="confirmation.config.title"
        :description="confirmation.config.description"
        :details="confirmation.config.details"
        :warning-text="confirmation.config.warningText"
        :confirm-text="confirmation.config.confirmText"
        :cancel-text="confirmation.config.cancelText"
        :confirm-variant="confirmation.config.confirmVariant"
        :cancel-variant="confirmation.config.cancelVariant"
        :icon-type="confirmation.config.iconType"
        :show-icon="confirmation.config.showIcon"
        :show-cancel="confirmation.config.showCancel"
        :size="confirmation.config.size"
        :closable="confirmation.config.closable"
        :loading="confirmation.loading.value"
        :require-input="confirmation.config.requireInput"
        :input-label="confirmation.config.inputLabel"
        :input-placeholder="confirmation.config.inputPlaceholder"
        :input-type="confirmation.config.inputType"
        :input-helper="confirmation.config.inputHelper"
        :expected-input="confirmation.config.expectedInput"
        @confirm="confirmation.handleConfirm"
        @cancel="confirmation.handleCancel"
        @close="confirmation.handleClose"
      />
    </div>
  </HLContentWrap>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import { useGlobalConfirmation } from '@/composables/useConfirmation'
import TopBar from '@/components/TopBar.vue'
import Sidebar from '@/components/layout/sidebar.vue'
import ConfirmationDialog from '@/components/ui/ConfirmationDialog.vue'
import KaizenModal from '@/components/ui/KaizenModal.vue'
import LoadingScreen from '@/components/ui/LoadingScreen.vue'
import '@platform-ui/highrise/style.css'
import './assets/css/theme.css'
import { HLContentWrap, HLText } from '@platform-ui/highrise'
import {MaterialWarningIcon} from '@gohighlevel/ghl-icons/24/material/rounded'// Stores
const authStore = useAuthStore()
const themeStore = useThemeStore()
// Global confirmation
const confirmation = useGlobalConfirmation()

// Router
const route = useRoute()

// Reactive data
const loading = ref(true)
const sidebarOpen = ref(false)

// Small-screen warning state. We re-show the modal each time the viewport
// crosses back below the breakpoint, but stay quiet while the user resizes
// within the small range after they've dismissed it.
const MIN_SUPPORTED_WIDTH = 768
const showSmallScreenWarning = ref(false)
const smallScreenDismissed = ref(false)

const handleSmallScreenCheck = () => {
  const isSmall = window.innerWidth < MIN_SUPPORTED_WIDTH
  if (isSmall && !smallScreenDismissed.value) {
    showSmallScreenWarning.value = true
  } else if (!isSmall) {
    showSmallScreenWarning.value = false
  }
}

const dismissSmallScreenWarning = () => {
  showSmallScreenWarning.value = false
  smallScreenDismissed.value = true
}

// Computed properties
const isAuthenticated = computed(() => authStore.isAuthenticated)

// Check if current route is an auth page (login, register, etc.)
const isAuthPage = computed(() => {
  const authRoutes = ['Login', 'OldLogin', 'Register', 'ForgotPassword', 'ResetPassword']
  return authRoutes.includes(route.name)
})

const isDashBoard = computed(()=>{
  return route.name == 'Dashboard'
})

// Check if current route is a project-related page
// Hide global TopNav on all project routes since ProjectDetail provides its own TopNav with project name
const isProjectDetailPage = computed(() => {
  const projectRoutes = [
    // Project main pages
    'ProjectDetail',
    'ProjectOverview',
    'ProjectFeatures',
    'ProjectTestCases',
    'ProjectTestRuns',
    'ProjectBugs',
    'ProjectSettings',
    'ProjectUsers',
    'ProjectTeam',
    'ProjectAccessibility',
    // Feature pages
    'CreateFeature',
    'FeatureDetail',
    // Test Case pages
    'CreateTestCase',
    'TestCaseDetail',
    // Test Run pages
    'CreateTestRun',
    'EditTestRun',
    'TestRunDetail',
    'ProjectTestRunDetail',
    // Bug pages
    'CreateBug',
    'BugDetail',
    'ProjectBugDetail',
    // Deleted pages
    'DeletedTestRuns'
  ]
  return projectRoutes.includes(route.name)
})

// Check if sidebar should be shown based on current route
const showSidebar = computed(() => {
  // Sidebar is not required for any page
  return false
})

// Methods
const toggleSidebar = () => {
  sidebarOpen.value = !sidebarOpen.value
}

const closeSidebar = () => {
  sidebarOpen.value = false
}

const scrollToTop = () => {
  const mainContainer = document.querySelector('.flex-1.overflow-x-hidden.overflow-y-auto')
  if (mainContainer) {
    mainContainer.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

// Watch for route changes to scroll to top
watch(() => route.name, () => {
  scrollToTop()
}, { immediate: true })

// Warm the chunk cache for the most-visited routes during browser idle time so
// the first navigation doesn't pay the chunk fetch + Vite compile cost.
const prefetchTopRoutes = () => {
  const prefetchers = [
    () => import('@/views/dashboard/Dashboard.vue'),
    () => import('@/views/my-tasks/MyTasks.vue'),
    () => import('@/views/projects/ProjectOverview.vue'),
    () => import('@/views/projects/ProjectTestRuns.vue'),
    () => import('@/views/projects/ProjectTestCases.vue'),
    () => import('@/views/projects/ProjectBugs.vue'),
    () => import('@/views/projects/ProjectFeatures.vue'),
    () => import('@/views/test-runs/TestRunDetail.vue'),
    () => import('@/views/bugs/BugDetail.vue'),
    () => import('@/views/test-cases/TestCaseDetail.vue'),
  ]
  const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 200))
  prefetchers.forEach((load, i) => {
    schedule(() => { load().catch(() => {}) }, { timeout: 2000 + i * 200 })
  })
}

// Lifecycle
onMounted(async () => {
  const MIN_LOADING_MS = 500
  const start = Date.now()

  handleSmallScreenCheck()
  window.addEventListener('resize', handleSmallScreenCheck)

  try {
    // Initialize theme
    themeStore.initializeTheme()

    // Initialize authentication
    await authStore.initializeAuth()
  } catch (error) {
    console.error('App initialization error:', error)
  } finally {
    const elapsed = Date.now() - start
    const remaining = MIN_LOADING_MS - elapsed
    if (remaining > 0) {
      await new Promise(resolve => setTimeout(resolve, remaining))
    }
    loading.value = false
    if (authStore.isAuthenticated) {
      prefetchTopRoutes()
    }
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', handleSmallScreenCheck)
})
</script>

<style>


/* Global styles */
html {
  scroll-behavior: smooth;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Dark mode scrollbar */
.dark ::-webkit-scrollbar-thumb {
  background: #475569;
}

.dark ::-webkit-scrollbar-thumb:hover {
  background: #64748b;
}

/* Focus styles */
.focus-ring {
  @apply focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900;
}

/* Transition utilities */
.transition-all {
  transition: all 0.2s ease-in-out;
}

.transition-colors {
  transition: color 0.2s ease-in-out, background-color 0.2s ease-in-out, border-color 0.2s ease-in-out;
}

.transition-transform {
  transition: transform 0.2s ease-in-out;
}

.transition-opacity {
  transition: opacity 0.2s ease-in-out;
}

/* Reduce backdrop blur for all modals - minimal to no blur */
.n-modal-mask,
.n-modal-mask-wrapper,
.hr-modal-mask,
.hr-modal-backdrop,
[class*="modal-mask"],
[class*="modal-backdrop"],
[class*="n-base"],
.n-base,
.hr-modal {
  backdrop-filter: blur(0px) !important;
  -webkit-backdrop-filter: blur(0px) !important;
  filter: blur(0px) !important;
}

/* Target any element with inline backdrop-filter style */
body [style*="backdrop-filter"],
body [style*="backdropFilter"] {
  backdrop-filter: blur(0px) !important;
  -webkit-backdrop-filter: blur(0px) !important;
}

/* Ensure modal overlays have minimal blur */
[class*="overlay"],
[data-modal-overlay] {
  backdrop-filter: blur(0px) !important;
  -webkit-backdrop-filter: blur(0px) !important;
}
</style>
<style scoped>
.kaizen-app {
  background-color: var(--secondary-25);
}
.kaizen-app-content {
  background-color: var(--secondary-25);
  padding: 1.75rem;
}
.kaizen-app-dashboard{
  padding-top: 0;
}
</style>